'use client';

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { ContextMenu, type ContextMenuPoint, type ContextMenuState, type MenuEntry } from '../ui/ContextMenu';
import { DIVIDER } from '../ui/ActionMenu';
import { useTranslation } from '../../lib/i18n';
import { useSaveMyNavSettings } from '../../lib/mutations';
import { hiddenNavEntries, reorderPinnedConversation, reorderNavEntry, setNavEntryHidden } from '../../lib/navLayout';
import type { NavLayout } from '../../lib/types';
import type { NavEntry } from './navEntry';

/** Arranging the menu happens IN the menu. An entry is moved by dragging it where it belongs, hidden
 *  from its own context menu, and brought back from that same menu on empty space.
 *
 *  There is deliberately no editor dialog and no settings page: a menu that needs a second surface to
 *  arrange it is one surface too many, and the arrangement is easiest to judge in the place it applies.
 *
 *  Only entries carrying an id can be customized — the layout addresses them by id, and an entry without
 *  one could be hidden but never brought back. */
export function useNavCustomization(allWorlds: NavEntry[], layout: NavLayout, displayOrder?: string[]) {
  const { t } = useTranslation();
  const save = useSaveMyNavSettings();
  const qc = useQueryClient();
  const [menu, setMenu] = useState<ContextMenuState | null>(null);

  // The stored order starts out empty, so the first edit has to seed it — and it must seed it with the
  // sequence the user is looking at. Seeding from the registry instead would silently rearrange a menu
  // whose surface sorts differently (the rail arranges by its spatial axis), which reads as the menu
  // reshuffling itself the moment you touch it. `displayOrder` is that surface's own sequence.
  const entryIds = useMemo(() => {
    const known = new Set(allWorlds.flatMap((entry) => (entry.id ? [entry.id] : [])));
    const shown = (displayOrder ?? []).filter((id) => known.has(id));
    const seen = new Set(shown);
    return [...shown, ...[...known].filter((id) => !seen.has(id))];
  }, [allWorlds, displayOrder]);
  const hidden = useMemo(() => hiddenNavEntries(allWorlds, layout), [allWorlds, layout]);

  // Every edit is computed from the layout the LAST edit produced, not from the one this render was
  // built with. Two quick edits — hide something, then immediately move something else — would
  // otherwise both start from the same base and the second would undo the first.
  //
  // The optimistic write is what makes that true: it is what lets the next edit see this one before
  // the round trip lands. Reading the cache back is the belt to that pair of braces, for the case
  // where the edit is fired again before React has re-rendered with the new value.
  const apply = (compute: (current: NavLayout) => NavLayout) => {
    const next = compute(qc.getQueryData<NavLayout>(['my-nav-settings']) ?? layout);
    qc.setQueryData(['my-nav-settings'], next);
    save.mutate(next);
  };

  /** The hidden spaces, each one click from coming back. This is the ONLY way back, so it hangs off
   *  every menu the navigation opens rather than only off empty space. */
  const hiddenGroup = (): MenuEntry[] => (hidden.length === 0 ? [] : [
    {
      label: t.nav.hiddenCount.replace('{count}', String(hidden.length)),
      icon: Eye,
      items: hidden.map((entry) => ({
        label: entry.label,
        icon: entry.icon,
        onClick: () => apply((current) => setNavEntryHidden(current, entry.id as string, false)),
      })),
    },
    DIVIDER,
  ]);

  const restoreItem = (): MenuEntry => ({
    action: 'reset',
    disabled: layout.hidden.length === 0 && layout.order.length === 0,
    onClick: () => apply((current) => ({ ...current, hidden: [], order: [] })),
  });

  const surfaceItems = (): MenuEntry[] => [...hiddenGroup(), restoreItem()];

  const reorderTo = (id: string, targetId: string) => apply((current) => reorderNavEntry(current, entryIds, id, targetId));
  /** `siblings` is the group the entry is drawn in, in the order the reader sees it: the arrows step to
   *  the neighbour on screen, whatever seat that neighbour holds in the stored order. */
  const entryItems = (entry: NavEntry, siblings: readonly { id?: string }[]): MenuEntry[] => {
    const id = entry.id;
    if (!id) return surfaceItems();
    const position = siblings.findIndex((candidate) => candidate.id === id);
    const above = position > 0 ? siblings[position - 1]?.id : undefined;
    const below = position >= 0 ? siblings[position + 1]?.id : undefined;
    // The arrows stay beside the drag: they are the only way to arrange the menu from a keyboard.
    return [
      { label: t.nav.hideEntry, icon: EyeOff, onClick: () => apply((current) => setNavEntryHidden(current, id, true)) },
      { label: t.nav.moveUp, icon: ChevronUp, disabled: !above, onClick: () => { if (above) reorderTo(id, above); } },
      { label: t.nav.moveDown, icon: ChevronDown, disabled: !below, onClick: () => { if (below) reorderTo(id, below); } },
      DIVIDER,
      ...surfaceItems(),
    ];
  };

  const openMenu = (point: ContextMenuPoint, items: MenuEntry[]) => setMenu({ ...point, items });

  const reorderPinTo = (pinnedIds: readonly string[], id: string, toIndex: number) =>
    apply(current => reorderPinnedConversation(current, pinnedIds, id, toIndex));
  /** A pinned conversation's menu: the conversation's own `actions`, then, with `moves`, the arrows that
   *  reorder the pins. The caller passes `moves` where the drag cannot run (the drawer, a touch screen):
   *  a mouse reorders by dragging and the owner wants its menu to hold only the conversation's actions. */
  const openPinMenu = (point: ContextMenuPoint, pinnedIds: readonly string[], id: string, actions: MenuEntry[], moves: boolean) => {
    const position = pinnedIds.indexOf(id);
    openMenu(point, [
      ...actions,
      ...(moves ? [
        DIVIDER,
        { label: t.nav.moveUp, icon: ChevronUp, disabled: position <= 0, onClick: () => reorderPinTo(pinnedIds, id, position - 1) },
        { label: t.nav.moveDown, icon: ChevronDown, disabled: position >= pinnedIds.length - 1, onClick: () => reorderPinTo(pinnedIds, id, position + 1) },
      ] : []),
    ]);
  };

  return {
    reorderPinTo,
    openPinMenu,
    /** A navigation entry's menu, opened by the entry's `ContextMenuTrigger` with the group it is drawn in. */
    openEntryMenu: (point: ContextMenuPoint, entry: NavEntry, siblings: readonly { id?: string }[]) => openMenu(point, entryItems(entry, siblings)),
    /** The menu of the navigation's empty area — the way to reach hidden entries — from its trigger and
     *  from the menu button. Hiding every entry leaves nothing to right-click ON, so that button is the
     *  way back. */
    openSurfaceMenu: (point: ContextMenuPoint) => openMenu(point, surfaceItems()),
    /** Commits a drag: put `id` into the seat `targetId` holds among the entries currently on show. */
    reorderTo,
    /** Rendered by the surface that owns the navigation. */
    overlays: menu ? <ContextMenu state={menu} onClose={() => setMenu(null)} /> : null,
  };
}
